import { useState } from "react";
import { Ban, Crown, Loader2, Undo2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuthor } from "@/hooks/useAuthor";
import { genUserName } from "@/lib/genUserName";
import type { MemberEntry } from "@/hooks/useMembers";

function PersonRow({ pubkey, children }: { pubkey: string; children?: React.ReactNode }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  return (
    <li className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
      <Avatar className="size-9 border">
        <AvatarImage src={metadata?.picture} alt={name} />
        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {metadata?.nip05 ?? `${pubkey.slice(0, 16)}…`}
        </p>
      </div>
      {children}
    </li>
  );
}

export function MembersAdmin({
  members,
  banlist,
  myPub,
  onBan,
  onUnban,
  pending,
}: {
  members: MemberEntry[];
  banlist: Set<string>;
  myPub: string;
  onBan: (pubkey: string) => void;
  onUnban: (pubkey: string) => void;
  pending: boolean;
}) {
  const [banTarget, setBanTarget] = useState<MemberEntry | null>(null);
  const banned = [...banlist];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Members ({members.length})</CardTitle>
          <CardDescription>
            Banning silences a member instantly in every Concord client (CORD-04
            Banlist). A full cryptographic removal (rekey) is coming later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {members.map((m) => (
              <PersonRow key={m.pubkey} pubkey={m.pubkey}>
                {m.isOwner ? (
                  <Crown className="size-4 text-gold" aria-label="Founder" />
                ) : (
                  m.pubkey !== myPub && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      aria-label="Ban member"
                      onClick={() => setBanTarget(m)}
                    >
                      <Ban className="size-4" />
                    </Button>
                  )
                )}
              </PersonRow>
            ))}
          </ul>
        </CardContent>
      </Card>

      {banned.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">Banned ({banned.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {banned.map((pk) => (
                <PersonRow key={pk} pubkey={pk}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() => onUnban(pk)}
                  >
                    {pending ? <Loader2 className="size-4 animate-spin" /> : <Undo2 className="size-4" />}
                    Unban
                  </Button>
                </PersonRow>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={banTarget !== null} onOpenChange={(o) => !o && setBanTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban this member?</AlertDialogTitle>
            <AlertDialogDescription>
              Their messages and actions vanish for every member immediately.
              They keep old keys until the community rekeys (coming soon), so
              treat this as silencing, not erasure.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (banTarget) onBan(banTarget.pubkey);
                setBanTarget(null);
              }}
            >
              Ban member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
